/** @jsxImportSource @emotion/react */
import { HomePageUsModal } from '@/components/modals/home-page-us.modal';
import { useModal } from '@/hooks/modal.hook';
import { useAppDispatch, useAppSelector } from '@/redux/hooks';
import { getListOptions, watchOptionsList } from '@/redux/slices/options.slice';
import {
  createHomePageUs,
  getPageByValue,
  watchPageByValue
} from '@/redux/slices/page.slice';
import { css } from '@emotion/react';
import { EyeOutlined } from '@ant-design/icons';
import { Col, Form, FormProps, Row, Typography } from 'antd';
import { useEffect } from 'react';
import { setMessages } from '@/redux/slices/app.slice';
import { Message } from '@/enums/message.enum';
import { formatDateToDDMMYYYY } from '@/utils/common';
import CKEditorMaster from '@/components/CKEditor/CKEditorMaster';
import { Button } from '@/components/button';
import usePermission from '@/hooks/permission.hook';

type FieldType = {
  contentHtml: string;
};

export default function HomeAboutUs() {
  const [form] = Form.useForm();
  const dispatch = useAppDispatch();
  const { openModal } = useModal();
  const homePageUs = useAppSelector(watchOptionsList);
  const listHomePageUs = useAppSelector(watchPageByValue);

  const { updatable } = usePermission();

  const onFinish: FormProps<FieldType>['onFinish'] = async (value) => {
    const response = await dispatch(
      createHomePageUs({
        value: homePageUs[0]?.value,
        code: homePageUs[0]?.code,
        content_html: value.contentHtml
      })
    );
    if (response.payload.status == '413') {
      dispatch(
        setMessages([
          {
            type: Message.ERROR,
            message:
              'Ảnh tải lên quá lớn. Vui lòng chọn ảnh có kích thước nhỏ hơn.'
          }
        ])
      );
    } else {
      await dispatch(getPageByValue('HP_CLIENT_ABOUTUS'));
      dispatch(
        setMessages([
          {
            type: Message.SUCCESS,
            message: 'Lưu thành công!'
          }
        ])
      );
    }
  };

  // const contentHtml = useMemo(() => {
  //   return form.getFieldValue('contentHtml');
  // }, [form]);

  const handleReset = () => {
    form.resetFields();
  };

  const handleOpenModal = () => {
    const contentHtml = form.getFieldValue('contentHtml');

    openModal(<HomePageUsModal contentHtml={contentHtml} />, {
      width: 1187
    });
  };

  useEffect(() => {
    dispatch(getListOptions({ value: 'HP_CLIENT_ABOUTUS' }));
    dispatch(getPageByValue('HP_CLIENT_ABOUTUS'));
  }, [dispatch]);

  useEffect(() => {
    if (listHomePageUs) {
      form.setFieldsValue(listHomePageUs);
    }
  }, [form, listHomePageUs]);

  return (
    <div css={rootStyles}>
      <Typography.Title css={titleStyles} level={5}>
        Trang Về chúng tôi
      </Typography.Title>
      <Row gutter={[10, 0]} justify='space-between'>
        <Col span={19}>
          <div css={boxLeftStyle}>
            <Form form={form} onFinish={onFinish}>
              <Form.Item name='contentHtml'>
                <CKEditorMaster
                  // Apply content HTML here
                  content={listHomePageUs?.contentHtml}
                  // Receive data content HTML change here
                  handleUpdateContent={(data) => {
                    form.setFieldValue('contentHtml', data);
                  }}
                />
              </Form.Item>
              <Row justify='end' css={{ gap: '1rem', marginTop: '3rem' }}>
                <Button
                  icon={<EyeOutlined />}
                  iconPosition='start'
                  onClick={handleOpenModal}
                >
                  Xem trước
                </Button>
                {updatable && (
                  <>
                    <Button danger onClick={handleReset}>
                      Hủy
                    </Button>
                    <Button
                      type='primary'
                      iconPosition='start'
                      htmlType='submit'
                    >
                      Lưu
                    </Button>
                  </>
                )}
              </Row>
            </Form>
          </div>
        </Col>
        <Col span={5}>
          <div css={boxRightStyle}>
            <Row css={rowItemRightStyle}>
              <Col span={14}>
                <Typography.Text css={boxTitleStyle}>
                  Người tạo:
                </Typography.Text>
              </Col>
              <Col span={10}>
                <Typography.Text css={boxSubTitleStyle}>
                  {listHomePageUs?.pageCreatedBy?.fullName}
                </Typography.Text>
              </Col>
            </Row>
            <Row css={rowItemRightStyle}>
              <Col span={14}>
                <Typography.Text css={boxTitleStyle}>Ngày tạo:</Typography.Text>
              </Col>
              <Col span={10}>
                <Typography.Text css={boxSubTitleStyle}>
                  {listHomePageUs?.createdDate
                    ? formatDateToDDMMYYYY(listHomePageUs?.createdDate)
                    : ''}
                </Typography.Text>
              </Col>
            </Row>
            <Row css={rowItemRightStyle}>
              <Col span={14}>
                <Typography.Text css={boxTitleStyle}>
                  Người cập nhật:
                </Typography.Text>
              </Col>
              <Col span={10}>
                <Typography.Text css={boxSubTitleStyle}>
                  {listHomePageUs?.pageModifiedBy?.fullName}
                </Typography.Text>
              </Col>
            </Row>
            <Row>
              <Col span={14}>
                <Typography.Text css={boxTitleStyle}>
                  Ngày cập nhật:
                </Typography.Text>
              </Col>
              <Col span={10}>
                <Typography.Text css={boxSubTitleStyle}>
                  {listHomePageUs?.modifiedDate
                    ? formatDateToDDMMYYYY(listHomePageUs?.modifiedDate)
                    : ''}
                </Typography.Text>
              </Col>
            </Row>
          </div>
        </Col>
      </Row>
    </div>
  );
}

const rootStyles = css`
  background: var(--gray-soft-color);
  display: flex;
  flex-direction: column;
  gap: 1.4rem;
  padding: 1.4rem 2.4rem;
  min-height: calc(100vh - var(--header-height));
`;

const titleStyles = css`
  padding: 1.4rem;
  background: var(--white-color);
  margin-bottom: 0 !important;
  border-radius: 1rem;
`;

const boxRightStyle = css`
  width: 24rem;
  background-color: rgba(255, 255, 255, 1);
  border: 1px solid rgba(240, 240, 240, 1);
  border-radius: 1rem;
  padding: 1.6rem;
`;

const boxLeftStyle = css`
  background-color: rgba(255, 255, 255, 1);
  border: 1px solid rgba(240, 240, 240, 1);
  border-radius: 1rem;
  padding: 1.5rem 2.7rem;
  height: auto;
  .quill {
    height: auto;
  }
`;

const boxTitleStyle = css`
  font-size: 1.4rem;
  font-weight: 400;
  color: rgba(0, 0, 0, 0.85);
`;

const boxSubTitleStyle = css`
  font-size: 1.4rem;
  font-weight: 400;
  color: rgba(0, 0, 0, 0.45);
`;

const rowItemRightStyle = css`
  margin-bottom: 0.8rem;
`;
